// Единый Route Guard модульной платформы (§7.1).
//
// ┌─ Почему компонентный guard, а не `beforeLoad` ─────────────────────────────┐
// │ План §7.1 предполагал `__root.beforeLoad` + `context.availability`. На     │
// │ фактическом коде это не работает: корневой роут объявлен через             │
// │ `createRootRoute` (не `…WithContext`), `beforeLoad` в нём нет, и ни один   │
// │ из 91 роута типизированного контекста не объявляет. Правило                │
// │ `frontend-state.md` §6 фиксирует это отдельным пунктом — guard через       │
// │ router-context уже пробовали и он молча редиректил всех (инцидент          │
// │ REVIEW-5 #12).                                                            │
// │                                                                           │
// │ Миграция на `createRootRouteWithContext` трогает `router.ts`, тип контекста│
// │ и потенциально каждый роут — цена, несопоставимая с выигрышем: guard       │
// │ обязан ждать карту доступности, а карта приходит из стора, который         │
// │ гидратируется синхронно СНАПШОТОМ и уточняется запросом. То есть решение   │
// │ всё равно реактивное, и «до монтирования» ничего не выигрывает.            │
// │                                                                           │
// │ Цена компонентного guard'а честная и ограниченная: закрытый раздел         │
// │ монтируется на один кадр, после чего заменяется замком либо уводится       │
// │ редиректом. Данных это не роняет — гейт создания живёт на сервере          │
// │ (`moduleGuard`), а не здесь.                                              │
// └───────────────────────────────────────────────────────────────────────────┘

import { toast } from "@studyhub/ui/toast";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { type ReactNode, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ModuleLockedScreen } from "@/components/modules/module-locked-screen";
import { ModuleStateNotice } from "@/components/modules/module-state-notice";
import { useModuleAvailability } from "@/hooks/use-module-availability";
import { useCommerceVisible } from "@/lib/module-availability/commerce";
import { presentedUnavailableReason } from "@/lib/module-availability/presented-reason";
import {
	availabilityOf,
	canOpenModule,
	fallbackRouteFor,
	matchModuleByPath,
	moduleLabelKey,
} from "@/lib/module-availability/resolve";

interface ModuleRouteGuardProps {
	children: ReactNode;
	/**
	 * Показывать плашку-объяснение над содержимым.
	 *
	 * Выключается на overlay-каркасах (прохождение теста, тренировка карточек,
	 * запись лекции): там страница — фиксированный по высоте экран без места под
	 * баннер, и плашка сломала бы каркас. Объяснение в этих режимах даёт сам
	 * экран, а редирект и замок работают одинаково везде.
	 */
	withNotice?: boolean;
}

export function ModuleRouteGuard({
	children,
	withNotice = true,
}: ModuleRouteGuardProps): React.ReactElement | null {
	const pathname = useRouterState({ select: (s) => s.location.pathname });
	const view = useModuleAvailability();
	const navigate = useNavigate();
	const { t } = useTranslation(["layout", "errors"]);

	const moduleId = matchModuleByPath(pathname);
	const availability = moduleId ? availabilityOf(view, moduleId) : null;
	// Закрыт только `hidden`; `read_only` пропускается всегда — пользователь
	// обязан видеть свои артефакты выключенного модуля.
	const isClosed = moduleId !== null && !canOpenModule(view, moduleId);
	const fallbackTo = moduleId && isClosed ? fallbackRouteFor(view, moduleId) : null;
	const commerceVisible = useCommerceVisible();
	// Причина для ТЕКСТА: в мобильной сборке тариф не называется (App Store 3.1.1).
	const reason =
		availability && availability.state !== "available"
			? presentedUnavailableReason(availability.reason, commerceVisible)
			: null;

	// Редирект — эффектом, а не в теле рендера: навигация во время рендера
	// роняет TanStack Router в предупреждение и гонку с его же переходом.
	useEffect(() => {
		if (!moduleId || !fallbackTo || !reason) return;
		const labelKey = moduleLabelKey(moduleId);
		const moduleName = labelKey ? t(labelKey, { ns: "layout" }) : moduleId;
		// Причина обязательна: раздел, который «просто исчез», читается как
		// потеря данных. `id` — дедуп sonner: при быстром возврате по истории
		// тост не наслаивается сам на себя.
		toast(t(`moduleState.${reason}.title`, { ns: "layout", module: moduleName }), {
			description: t(`moduleUnavailable.${reason}`, { ns: "errors" }),
			id: `module-unavailable-${moduleId}`,
		});
		void navigate({ to: fallbackTo, replace: true });
	}, [moduleId, fallbackTo, reason, navigate, t]);

	if (!moduleId || !availability) return <>{children}</>;
	if (availability.state === "hidden") return null;

	if (availability.state === "teaser") {
		return (
			<ModuleLockedScreen
				moduleId={moduleId}
				// Отступы — семантическими токенами страницы, чтобы замок стоял в том
				// же ритме, что и содержимое раздела, которое он замещает.
				className="mx-auto w-full max-w-narrow px-page-x py-page-y lg:px-page-x-lg"
			/>
		);
	}

	// `available` — раздел работает, добавлять к нему нечего. Ранний выход
	// обязателен: обёртка плашки ниже несёт `pt-page-y`, и при `available`
	// (`ModuleStateNotice` возвращает `null`) она давала ПУСТОЙ отступ сверху на
	// КАЖДОМ модульном роуте. На обычных страницах он читался как чуть больший
	// воздух, а в полноэкранном воркспейсе курса — как необъяснимая белая полоса
	// над шапкой, из-за которой bounded-каркас `h-dvh` перерастал экран и
	// появлялся вертикальный скролл всей страницы (жалоба 2026-08-09).
	if (availability.state === "available") return <>{children}</>;

	// `read_only`: артефакты видны, создание закрыто сервером. Плашка объясняет,
	// почему кнопки создания не работают.
	return (
		<>
			{withNotice && (
				<div className="px-page-x pt-page-y lg:px-page-x-lg">
					<ModuleStateNotice moduleId={moduleId} availability={availability} />
				</div>
			)}
			{children}
		</>
	);
}
